import { useMemo, useState } from 'react'
import s from './Details.scss'
import { Container, Row, Col, Button } from 'reactstrap'
import {
  KeyValuePairs,
  GoogleAutocomplete,
  Input,
  Checkbox,
  DatePicker as IdealDatePicker
} from 'components/ideal-core-ui'
import Select, { components } from 'react-select'
import { Map } from './Map'
import {
  fromConstantPropertyToSelectOptions,
  fromConstantPropertyToSelectValue
} from 'utils/serializers'
import {
  FaEnvelope,
  FaTrashAlt,
  FaCertificate,
  FaRegClone,
  FaExpandAlt,
  FaCompressAlt,
  FaLink
} from 'react-icons/fa'
import { APP_CONSTANTS, BID_SUBMISSION_STATUS } from 'utils/constants'
import get from 'lodash/get'
import { NumericFormat } from 'react-number-format'
import DatePicker from 'react-datepicker'
import '!style-loader!css-loader!react-datepicker/dist/react-datepicker.css' // eslint-disable-line
import { CompanyClients } from 'components/common/CompanyClients'
import { Offices } from 'components/common/Offices'
import { toDealInternalTeam } from '../../serializers'
import { FollowupDateModal } from 'components/common/modals/FollowupDateModal'
import { PaginatedSelect } from 'components/common/PaginatedSelect'
import { toCompaniesSelect } from 'components/Companies/serializers'
import { getDateFormat, isAdmin, getUser } from 'utils/authService'
import { utcDate, timezoneDate } from 'utils/helpers'
import { InternalTeam } from 'components/common/InternalTeam'
import { HealthWarningModal } from './HealthWarningModal'
import cn from 'classnames'
import moment from 'moment-timezone'
import swal from 'sweetalert'
import { useSelector } from 'react-redux'

const Details = ({
  deal,
  users,
  internalTeam,
  dealAwarded,
  addInternalTeamUser,
  removeInternalTeamUser,
  dealCategories,
  dealWorkTypes,
  dealBidStatuses,
  dealProjectStatuses,
  dealSources,
  dealScopes,
  dealNotBiddingReasons,
  dealReasons,
  updateState,
  onSelectCallback,
  onSave,
  showDealFollowup,
  orderedStatuses,
  showHealthError,
  changeProperty,
  changeFollowUp,
  changeClientContacts,
  refreshClientContacts,
  changeClientProperty,
  changeContactIsMain,
  changeClientContactRole,
  state,
  setState,
  updateDealStatusFollowupDate,
  toggleFollowupDateModal,
  changeStatus,
  toggleHealthErrorModal,
  getBidStatus,
  changeProperties,
  scheduleTaskForAwardedCompany,
  toggleFollowupByEmailModal,
  deleteDeal,
  toggleCloneOpportunityModal,
  toggleLinkOpportunityModal,
  changeBidStatus,
  generateQuoteNumber,
  usingQuoteNumberAutomation,
  generateProjectNumber,
  usingProjectNumberAutomation,
  settings,
  geographicalAreas,
  dealContractTypes,
  buildingOwners,
  endUsers,
  propertyManagers,
  changeSpecialClientsProperty,
  internalBidStatuses
}) => {
  const { address: userAddress } = useSelector(state => state.header)
  const [expanded, setExpanded] = useState(false)
  const loggedUser = getUser()
  const canDelete =
    isAdmin() ||
    loggedUser?.sub === deal.createdBy?.email ||
    loggedUser?.id === deal.dealManager?.value

  const MultiValueLabel = props =>
    useMemo(() => {
      return (
        <>
          {deal.awardedCompany?.value === props.data.value && (
            <FaCertificate className={s.awardIcon} title="Opportunity Awarded" />
          )}
          <components.MultiValueLabel {...props} />
        </>
      )
    }, [props])

  const calculateDistanceToSite = () => {
    const lat = deal.latitude
    const long = deal.longitude
    const google = window.google
    const latLngObj = new google.maps.LatLng(lat, long)

    if (userAddress && latLngObj) {
      var service = new google.maps.DistanceMatrixService()
      service.getDistanceMatrix(
        {
          origins: [userAddress],
          destinations: [latLngObj],
          travelMode: 'DRIVING',
          avoidHighways: false,
          unitSystem: google.maps.UnitSystem.IMPERIAL
        },
        (response, status) => {
          if (status !== 'OK' || response.rows[0].elements[0].status === 'ZERO_RESULTS') {
            swal(
              'The distance cannot be calculated',
              'Please check the address and try again, or manually add the distance'
            )
            return
          }
          const distance = response.rows[0].elements[0].distance.text
          updateState(distance.split(' ')[0].replace(',', ''), 'distanceToSite')
        }
      )
    }
  }

  return (
    <Container className={s.container}>
      <Button className={s.emailButton} onClick={toggleFollowupByEmailModal}>
        <FaEnvelope style={{ position: 'relative', bottom: 2, right: 4 }} />
        Follow-up by Email
      </Button>
      {canDelete && (
        <>
          <div title="Delete" className={s.deleteIcon} onClick={deleteDeal}>
            <FaTrashAlt />
          </div>
          <div title="Clone" className={s.cloneIcon} onClick={toggleCloneOpportunityModal}>
            <FaRegClone />
          </div>
          <div title="Link" className={s.cloneIcon} onClick={toggleLinkOpportunityModal}>
            <FaLink />
          </div>
        </>
      )}
      <Row className={s.row}>
        <Col className={s.column} lg="4" xs="12" sm="6">
          <p className={s.title}>Opportunity Name and Description</p>
          <KeyValuePairs
            pairs={[
              {
                key: '* Opportunity Name',
                value: (
                  <Input
                    type="text"
                    text={deal.name || ''}
                    className={s.nameInput}
                    placeholder="Enter Opportunity Name"
                    onChangeCb={() => updateState(event.target.value, 'name')}
                    cy="deal_name_input"
                  />
                )
              },
              {
                key: (
                  <>
                    <span>Distance to Site (miles)</span>
                    <br />
                    {deal.latitude && deal.longitude && !deal.distanceToSite && (
                      <span className={s.link} onClick={calculateDistanceToSite}>
                        calculate distance to site
                      </span>
                    )}
                  </>
                ),
                value: (
                  <Input
                    type="number"
                    text={deal.distanceToSite || ''}
                    classname={s.nameInput}
                    placeholder="Enter Distance to Site"
                    onChangeCb={() => updateState(event.target.value, 'distanceToSite')}
                  />
                )
              },
              {
                key: 'Geographical Area',
                value: (
                  <Select
                    onChange={value => changeProperty(value, 'geographicalArea')}
                    options={geographicalAreas}
                    value={deal.geographicalArea}
                  />
                )
              },
              {
                key: 'Brief Note',
                value: (
                  <Input
                    type="textarea"
                    text={deal.briefNote || ''}
                    classname={s.briefNote}
                    placeholder="Enter Brief Note"
                    onChangeCb={() => updateState(event.target.value, 'briefNote')}
                    cy="deal_note_input"
                  />
                )
              }
            ]}
          />
        </Col>
        <Col className={s.column} lg="4" xs="12" sm="6">
          <p className={s.title}>Opportunity Location</p>
          <KeyValuePairs
            pairs={[
              {
                key: 'Address',
                value: (
                  <GoogleAutocomplete
                    value={deal.address || ''}
                    onChange={value => updateState(value, 'address')}
                    onSelectCallback={onSelectCallback}
                    cy="address_input"
                  />
                )
              },
              {
                key: 'Street Address',
                value: (
                  <Input
                    type="text"
                    text={deal.address2 ? deal.address2.trim() : ''}
                    className={s.nameInput}
                    placeholder="Street 1"
                    onChangeCb={() => updateState(event.target.value, 'address2')}
                    cy="street_address"
                  />
                )
              },
              {
                key: 'Street 2',
                value: (
                  <Input
                    type="text"
                    text={deal.street2 ? deal.street2.trim() : ''}
                    className={s.nameInput}
                    placeholder="Street 2"
                    onChangeCb={() => updateState(event.target.value, 'street2')}
                    cy="street_address2"
                  />
                )
              },
              {
                key: '* City',
                value: (
                  <Input
                    type="text"
                    text={deal.city || ''}
                    className={s.nameInput}
                    placeholder="Enter City"
                    onChangeCb={() => updateState(event.target.value, 'city')}
                    cy="city"
                  />
                )
              },
              {
                key: '* State/Province/Region',
                value: (
                  <Input
                    type="text"
                    text={deal.state || ''}
                    className={s.nameInput}
                    placeholder="Enter State/Province/Region"
                    onChangeCb={() => updateState(event.target.value, 'state')}
                    cy="state"
                  />
                )
              },
              {
                key: 'Zip/Postal Code',
                value: (
                  <Input
                    type="text"
                    text={deal.zipCode || ''}
                    className={s.nameInput}
                    placeholder="Enter Zip/Postal Code"
                    onChangeCb={() => updateState(event.target.value, 'zipCode')}
                    cy="zip"
                  />
                )
              },
              {
                key: 'Country',
                value: (
                  <Input
                    type="text"
                    text={deal.country || ''}
                    className={s.nameInput}
                    placeholder="Enter Country"
                    onChangeCb={() => updateState(event.target.value, 'country')}
                    cy="country"
                  />
                )
              }
            ]}
          />
        </Col>
        <Col className={s.column} lg="4" xs="12" sm="12">
          <p className={s.title}>Map</p>
          <div className={s.mapWrapper}>
            <Map lat={deal.latitude} lng={deal.longitude} />
          </div>
        </Col>
        <Button className={s.saveBtn} onClick={() => onSave()} data-cy="update_deal">
          Save
        </Button>
      </Row>
      <Row className={s.row}>
        <Col className={s.column} lg="6" xs="12" sm="6">
          <div className={s.descriptionWrapper}>
            <p className={s.title}>Description</p>
            {!expanded ? (
              <FaExpandAlt className={s.changeSize} onClick={() => setExpanded(true)} />
            ) : (
              <FaCompressAlt className={s.changeSize} onClick={() => setExpanded(false)} />
            )}
            <Input
              type="textarea"
              text={deal.description || ''}
              classname={cn(s.description, { [s.expanded]: expanded })}
              placeholder="Enter Description"
              onChangeCb={() => updateState(event.target.value, 'description')}
              cy="deal_description_input"
            />
            <p className={s.title}>Scope(s)</p>
            <Select
              value={deal.dealScopes}
              onChange={values => changeProperty(values, 'dealScopes')}
              options={dealScopes || []}
              isMulti
            />
          </div>
        </Col>
        <Col className={s.column} lg="6" xs="12" sm="6">
          <InternalTeam
            dealInternalTeam={toDealInternalTeam(deal.internalTeam)}
            removeInternalTeamUser={removeInternalTeamUser}
            changeUserCb={value => {
              setState({
                ...state,
                internalTeam: {
                  ...internalTeam,
                  user: value
                }
              })
            }}
            changeRoleCb={value => {
              setState({
                ...state,
                internalTeam: {
                  ...internalTeam,
                  role: value
                }
              })
            }}
            internalTeam={internalTeam}
            users={users}
            addInternalTeamUser={userRole =>
              addInternalTeamUser(userRole, () => {
                setState({
                  ...state,
                  internalTeam: {
                    user: '',
                    role: ''
                  }
                })
              })
            }
          />
          <KeyValuePairs
            pairs={[
              {
                key: 'Building Owner(s)',
                keyClass: [s.keyClass],
                value: (
                  <Select
                    value={deal.buildingOwners}
                    onChange={values =>
                      changeSpecialClientsProperty(values, 'buildingOwners', true)
                    }
                    options={buildingOwners || []}
                    isMulti
                  />
                )
              },
              {
                key: 'End User(s)',
                keyClass: [s.keyClass],
                value: (
                  <Select
                    value={deal.endUsers}
                    onChange={values => changeSpecialClientsProperty(values, 'endUsers', true)}
                    options={endUsers || []}
                    isMulti
                  />
                )
              },
              {
                key: 'Property Manager(s)',
                keyClass: [s.keyClass],
                value: (
                  <Select
                    value={deal.propertyManagers}
                    onChange={values =>
                      changeSpecialClientsProperty(values, 'propertyManagers', true)
                    }
                    options={propertyManagers || []}
                    isMulti
                  />
                )
              }
            ]}
          />
        </Col>
        <Button className={s.saveBtn} onClick={() => onSave()} data-cy="update_deal">
          Save
        </Button>
      </Row>
      <Row className={s.row}>
        <FollowupDateModal
          entity={deal}
          onSave={updateDealStatusFollowupDate}
          showFollowupDateModal={showDealFollowup}
          toggleFollowupDateModal={toggleFollowupDateModal}
          title="Opportunity"
          cancelCb={toggleFollowupDateModal}
        />
        <p className={s.logisticTitle}>
          <span className={s.title}>Opportunity Logistics:</span>{' '}
          <i className={s.subtitle}>
            Due Date, Opportunity Manager, Status and other &#39;logistics&#39; information that
            will help you keep this opportunity moving, work on it when needed, and follow up on it
            at appropriate times.
          </i>
        </p>
        <Col className={s.column} lg="4" xs="12" sm="6">
          <KeyValuePairs
            pairs={[
              {
                key: (
                  <>
                    <span>Quote Number</span>
                    <br />
                    {usingQuoteNumberAutomation && !deal.quoteNumber && (
                      <span className={s.link} onClick={generateQuoteNumber}>
                        generate quote no
                      </span>
                    )}
                  </>
                ),
                value: (
                  <Input
                    type="text"
                    text={(deal.quoteNumber && String(deal.quoteNumber)) || ''}
                    className={s.nameInput}
                    placeholder="Enter Quote Number"
                    onChangeCb={() =>
                      updateState(event.target.value.replace(/[^a-zA-Z0-9_-]/g, ''), 'quoteNumber')
                    }
                  />
                )
              },
              {
                key: 'Bid Due Date',
                valueClass: [s.dueDateWrapper],
                value: (
                  <DatePicker
                    selected={moment(
                      timezoneDate(deal.dueDate, 'HH:mm:ss'),
                      `${getDateFormat()} HH:mm:ss`
                    ).toDate()}
                    startDate={moment(
                      timezoneDate(deal.dueDate, 'HH:mm:ss'),
                      `${getDateFormat()} HH:mm:ss`
                    ).toDate()}
                    onChange={value => {
                      if (!value) return
                      changeProperty(utcDate(value), 'dueDate')
                    }}
                    showTimeSelect
                    timeFormat="h:mm aa"
                    timeIntervals={15}
                    dateFormat={`${getDateFormat()
                      .replace('YYYY', 'yyyy')
                      .replace('DD', 'dd')} h:mm aa`}
                    timeCaption="time"
                    placeholderText="Select deal due date"
                    className={s.dueDate}
                    id="cy_bid_due_date"
                  />
                )
              },
              {
                key: 'Follow-up date',
                value: !deal.followUpDate ? (
                  '-'
                ) : (
                  <IdealDatePicker
                    value={deal.followUpDate && timezoneDate(deal.followUpDate)}
                    onChange={value => {
                      const date = value && value !== null ? utcDate(value) : ''
                      changeFollowUp(date)
                    }}
                    cy="cy_follow_up_date"
                  />
                )
              },
              {
                key: 'Show on Follow-up Calendar',
                value: (
                  <Checkbox
                    isChecked={deal.followUpCalendar || false}
                    triggerCheckbox={value =>
                      changeProperty(value.target.checked, 'followUpCalendar')
                    }
                    cy="show_on_follow_up_calendar"
                  />
                )
              },
              {
                key: '* Opportunity Status',
                value: (
                  <Select
                    value={deal.status}
                    onChange={value => changeStatus(value)}
                    options={orderedStatuses}
                  />
                )
              },
              {
                key: 'Opportunity Manager',
                value: (
                  <Select
                    onChange={value => changeProperty(value, 'dealManager')}
                    options={users}
                    value={deal.dealManager}
                  />
                )
              },
              {
                key: 'Client Bid Stage',
                value: (
                  <Select
                    onChange={value => changeBidStatus(value)}
                    options={dealBidStatuses}
                    value={deal.bidStatus}
                    id="cy_client_bid_stage"
                  />
                )
              },
              {
                key: 'Internal Bid Status',
                value: (
                  <Select
                    onChange={value => changeProperty(value, 'internalBidStatus')}
                    options={internalBidStatuses}
                    value={deal.internalBidStatus}
                  />
                )
              },
              {
                key: 'Opportunity Size',
                value: (
                  <Select
                    value={fromConstantPropertyToSelectValue(deal.size, 'DealSize')}
                    onChange={value => changeProperty(value, 'size')}
                    options={fromConstantPropertyToSelectOptions(
                      Object.keys(APP_CONSTANTS.DealSize),
                      'DealSize'
                    )}
                  />
                )
              },
              {
                key: 'Opportunity Category',
                value: (
                  <Select
                    onChange={value => changeProperty(value, 'dealCategory')}
                    options={dealCategories}
                    value={deal.dealCategory}
                  />
                )
              },
              {
                key: 'Work Type',
                value: (
                  <Select
                    value={deal.workType}
                    onChange={value => changeProperty(value, 'workType')}
                    options={dealWorkTypes}
                  />
                )
              },
              {
                key: 'Contract Type',
                value: (
                  <Select
                    onChange={value => changeProperty(value, 'dealContractType')}
                    options={dealContractTypes}
                    value={deal.dealContractType}
                  />
                )
              }
            ]}
          />
        </Col>
        <Col className={s.column} lg="4" xs="12" sm="6">
          <HealthWarningModal
            toggleHealthWarningModal={toggleHealthErrorModal}
            showHealthWarningModal={showHealthError}
          />
          <Row>
            <Col className={s.column} xl="6" lg="12" xs="12" sm="12" widths={[{ xl: '1400px' }]}>
              <KeyValuePairs
                pairs={[
                  {
                    key: 'Bid Submission Date',
                    valueClass: [s.dueDateWrapper],
                    value: (
                      <DatePicker
                        selected={
                          deal.submissionDate &&
                          moment(
                            timezoneDate(deal.submissionDate, 'HH:mm:ss'),
                            `${getDateFormat()} HH:mm:ss`
                          ).toDate()
                        }
                        startDate={
                          deal.submissionDate &&
                          moment(
                            timezoneDate(deal.submissionDate, 'HH:mm:ss'),
                            `${getDateFormat()} HH:mm:ss`
                          ).toDate()
                        }
                        onChange={value => {
                          changeProperty(value ? utcDate(value) : null, 'submissionDate')
                        }}
                        showTimeSelect
                        timeFormat="h:mm aa"
                        timeIntervals={15}
                        dateFormat={`${getDateFormat()
                          .replace('YYYY', 'yyyy')
                          .replace('DD', 'dd')} h:mm aa`}
                        timeCaption="time"
                        placeholderText="Select Bid Submission Date"
                        className={s.dueDate}
                      />
                    )
                  },
                  {
                    key: 'Bid Submission Status',
                    value: <span>{BID_SUBMISSION_STATUS[deal.bidSubmissionStatus]}</span>
                  }
                ]}
              />
              <div className={s.dealAwarded}>
                Opportunity Awarded:
                <input
                  type="radio"
                  checked={dealAwarded}
                  onChange={() => {
                    setState({
                      dealAwarded: true
                    })
                  }}
                  data-cy="set_deal_awarded"
                />{' '}
                Yes
                <input
                  type="radio"
                  checked={!dealAwarded}
                  onChange={() => {
                    if (!deal.awardedCompany) {
                      setState({
                        dealAwarded: false
                      })
                      return
                    }
                    const [status, shouldUpdateBidStatus] = getBidStatus({
                      value: 2,
                      label: 'Bidding'
                    })
                    changeProperties({
                      dealAwarded: false,
                      awardedCompany: null,
                      ...(shouldUpdateBidStatus ? { bidStatus: status } : {})
                    })
                  }}
                  data-cy="unset_deal_awarded"
                />{' '}
                No
                {dealAwarded && (
                  <PaginatedSelect
                    url="/v1/companies"
                    className={s.clients}
                    value={deal.awardedCompany}
                    onChange={value => {
                      const [status, shouldUpdateBidStatus] = getBidStatus({
                        value: 3,
                        label: 'Bidding Awarded'
                      })
                      changeProperties({
                        awardedCompany: value,
                        ...(shouldUpdateBidStatus && { bidStatus: status })
                      })
                      scheduleTaskForAwardedCompany(value)
                    }}
                    serializer={toCompaniesSelect}
                    id="company_type_select_cy"
                  />
                )}
              </div>
              <KeyValuePairs
                pairs={[
                  ...(deal.wonOn
                    ? [
                        {
                          key: 'Won On',
                          value: (
                            <IdealDatePicker
                              value={deal.wonOn && timezoneDate(deal.wonOn)}
                              onChange={value => {
                                const date = value && value !== null ? utcDate(value) : ''
                                changeProperty(date, 'wonOn')
                              }}
                            />
                          )
                        }
                      ]
                    : []),
                  ...(deal.lostOn
                    ? [
                        {
                          key: 'Lost On',
                          value: (
                            <IdealDatePicker
                              value={deal.lostOn && timezoneDate(deal.lostOn)}
                              onChange={value => {
                                const date = value && value !== null ? utcDate(value) : ''
                                changeProperty(date, 'lostOn')
                              }}
                            />
                          )
                        }
                      ]
                    : []),
                  {
                    key: 'Opportunity Priority',
                    value: (
                      <Select
                        value={fromConstantPropertyToSelectValue(deal.priority, 'DealPriority')}
                        onChange={value => changeProperty(value, 'priority')}
                        options={fromConstantPropertyToSelectOptions(
                          Object.keys(APP_CONSTANTS.DealPriority),
                          'DealPriority'
                        )}
                      />
                    )
                  },
                  {
                    key: 'Opportunity Health',
                    value: (
                      <Select
                        value={fromConstantPropertyToSelectValue(deal.health, 'DealHealth')}
                        onChange={value => changeProperty(value, 'health')}
                        options={fromConstantPropertyToSelectOptions(
                          Object.keys(APP_CONSTANTS.DealHealth),
                          'DealHealth'
                        )}
                      />
                    )
                  },
                  {
                    key: 'Opportunity Source',
                    value: (
                      <Select
                        onChange={value => changeProperty(value, 'dealSource')}
                        options={dealSources}
                        value={deal.dealSource}
                      />
                    )
                  },
                  {
                    key: 'Square Footage',
                    value: (
                      <NumericFormat
                        thousandSeparator
                        value={deal.squarefootage}
                        onValueChange={formattedValue =>
                          updateState(formattedValue.value, 'squarefootage')
                        }
                      />
                    )
                  },
                  {
                    key: '# Stories',
                    value: (
                      <Input
                        type="text"
                        text={(deal.stories && String(deal.stories)) || ''}
                        className={s.nameInput}
                        placeholder="Enter Stories"
                        onChangeCb={() => updateState(event.target.value, 'stories')}
                      />
                    )
                  },
                  {
                    key: (
                      <>
                        <span>Job/Project Number</span>
                        <br />
                        {usingProjectNumberAutomation && !deal.projectNumber && (
                          <span className={s.link} onClick={generateProjectNumber}>
                            generate project no
                          </span>
                        )}
                      </>
                    ),
                    value: (
                      <Input
                        type="text"
                        text={deal.projectNumber || ''}
                        className={s.nameInput}
                        placeholder="Enter Job/Project Number"
                        onChangeCb={() =>
                          updateState(event.target.value, 'projectNumber', null, true)
                        }
                        cy="deal_project_number_input"
                      />
                    )
                  },
                  {
                    key: 'Project Status',
                    value: (
                      <Select
                        isClearable
                        onChange={value => changeProperty(value, 'dealProjectStatus')}
                        options={dealProjectStatuses}
                        value={deal.dealProjectStatus}
                      />
                    )
                  },
                  {
                    key: 'Project Start Date',
                    value: (
                      <IdealDatePicker
                        value={deal.projectStartDate && timezoneDate(deal.projectStartDate)}
                        onChange={value => {
                          const date = value && value !== null ? utcDate(value) : ''
                          changeProperty(date, 'projectStartDate')
                        }}
                        disabledDays={{
                          after: deal.projectEndDate && moment(deal.projectEndDate).toDate()
                        }}
                      />
                    )
                  },
                  {
                    key: 'Project End Date',
                    value: (
                      <IdealDatePicker
                        value={deal.projectEndDate && timezoneDate(deal.projectEndDate)}
                        onChange={value => {
                          const date = value && value !== null ? utcDate(value) : ''
                          changeProperty(date, 'projectEndDate')
                        }}
                        disabledDays={{
                          before: deal.projectStartDate && moment(deal.projectStartDate).toDate()
                        }}
                      />
                    )
                  },
                  {
                    key: 'Project Duration',
                    value: deal.projectDuration || 'N/A'
                  }
                ]}
              />
            </Col>
          </Row>
        </Col>
        <Col className={s.column} lg="4" xs="12" sm="12">
          <Offices
            dealTeams={deal.teams}
            teamsCb={teams => {
              changeProperty(teams, 'teams')
            }}
          />
          <KeyValuePairs
            pairs={[
              {
                key: 'Hard Bid',
                keyClass: [s.bidLabel],
                value: (
                  <Checkbox
                    isChecked={deal.hardBid || false}
                    triggerCheckbox={value => changeProperty(value.target.checked, 'hardBid')}
                  />
                )
              },
              {
                key: 'Sealed Bid',
                keyClass: [s.bidLabel],
                value: (
                  <Checkbox
                    isChecked={deal.sealedBid || false}
                    triggerCheckbox={value => changeProperty(value.target.checked, 'sealedBid')}
                  />
                )
              },
              {
                key: 'Prevailing Wage',
                keyClass: [s.bidLabel],
                value: (
                  <Checkbox
                    isChecked={deal.prevailingWage || false}
                    triggerCheckbox={value =>
                      changeProperty(value.target.checked, 'prevailingWage')
                    }
                  />
                )
              },
              settings?.showPublicPrivate
                ? {
                    key: 'Is Opportunity Public/Private',
                    value: (
                      <Select
                        onChange={value => changeProperty(value, 'visibility')}
                        options={[
                          { value: 'PUBLIC', label: 'Public' },
                          { value: 'PRIVATE', label: 'Private' }
                        ]}
                        value={deal.visibility}
                      />
                    )
                  }
                : {},
              {
                key: 'Date Created',
                value: timezoneDate(deal.createdAt)
              },
              {
                key: 'Last Modified',
                value: timezoneDate(deal.updatedAt)
              },
              {
                key: 'Created By',
                value: get(deal, 'createdBy.email', '-')
              },
              {
                key: 'Modified By',
                value: get(deal, 'updatedBy.email', '-')
              },
              {
                key: 'Reason for Not Bidding',
                value: (
                  <Select
                    onChange={value => changeProperty(value, 'dealNotBiddingReason')}
                    options={dealNotBiddingReasons}
                    value={deal.dealNotBiddingReason}
                  />
                )
              },
              {
                key: 'Reason for Not Winning',
                value: (
                  <Select
                    onChange={value => changeProperty(value, 'noWinReason')}
                    options={dealReasons}
                    value={deal.noWinReason}
                  />
                )
              },
              {
                key: 'Link To Plans http(s)://www.example.com',
                value: (
                  <>
                    <Input
                      type="text"
                      text={(deal.linkToPlans && String(deal.linkToPlans)) || ''}
                      className={s.nameInput}
                      placeholder="Enter Link To Plans"
                      onChangeCb={() => updateState(event.target.value, 'linkToPlans')}
                    />
                    <a href={deal.linkToPlans} target="_blank" rel="noopener noreferrer">
                      {deal.linkToPlans}
                    </a>
                  </>
                )
              },
              {
                key: 'Bid Reference Document',
                value: (
                  <Input
                    type="text"
                    text={(deal.bidReferenceDocument && String(deal.bidReferenceDocument)) || ''}
                    className={s.nameInput}
                    placeholder="Enter Bid Reference Document"
                    onChangeCb={() => updateState(event.target.value, 'bidReferenceDocument')}
                  />
                )
              }
            ]}
          />
        </Col>
        <div className={s.clearfix} />
        <Button className={s.saveBtn} onClick={() => onSave()}>
          Save
        </Button>
      </Row>
      <Row className={s.row}>
        <Col className={s.column} lg="12" xs="12" sm="12">
          <span className={s.title}>Clients and Contacts:</span>{' '}
          <i className={s.subtitle}>
            Below are listed the clients for this deal and the contacts for each company with their
            role for the deal.
          </i>
          <CompanyClients
            withInvitedToBid={settings?.showInvitedToBid}
            withRoles
            withEmailContact
            withSaveEmailAsNote
            changeClientContacts={changeClientContacts}
            refreshClientContacts={refreshClientContacts}
            entity={deal}
            changeClientProperty={changeClientProperty}
            changeContactIsMain={changeContactIsMain}
            changeClientContactRole={changeClientContactRole}
            id="company_select_cy"
            isClearable={false}
            customStyles={{
              multiValueRemove: base => ({
                ...base,
                ...(deal.clients.length === 1 || !canDelete ? { display: 'none' } : {})
              })
            }}
            components={{ MultiValueLabel }}
            awardedCompany={deal.awardedCompany}
          />
        </Col>
        <Button className={s.saveBtn} onClick={() => onSave()}>
          Save
        </Button>
      </Row>
      <Row className={cn(s.row, s.noBorder)}>
        <p className={s.title}>Building Design Team</p>
        <Col className={s.column} lg="4" xs="12" sm="12">
          <KeyValuePairs
            pairs={[
              {
                key: 'Owner',
                value: (
                  <Input
                    type="text"
                    text={deal.owner || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'owner')}
                  />
                )
              },
              {
                key: 'Developer',
                value: (
                  <Input
                    type="text"
                    text={deal.developer || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'developer')}
                  />
                )
              },
              {
                key: 'Architect',
                value: (
                  <Input
                    type="text"
                    text={deal.architect || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'architect')}
                  />
                )
              },
              {
                key: 'General Contractor',
                value: (
                  <Input
                    type="text"
                    text={deal.generalcontractor || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'generalcontractor')}
                  />
                )
              }
            ]}
          />
        </Col>
        <Col className={s.column} xl="4" lg="4" xs="12" sm="12" widths={[{ xl: '1400px' }]}>
          <KeyValuePairs
            pairs={[
              {
                key: 'Construction Mgr',
                value: (
                  <Input
                    type="text"
                    text={deal.constructionmgr || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'constructionmgr')}
                  />
                )
              },
              {
                key: 'Structural Eng',
                value: (
                  <Input
                    type="text"
                    text={deal.structuraleng || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'structuraleng')}
                  />
                )
              },
              {
                key: 'Civil Eng',
                value: (
                  <Input
                    type="text"
                    text={deal.civileng || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'civileng')}
                  />
                )
              },
              {
                key: 'Mechanical Eng',
                value: (
                  <Input
                    type="text"
                    text={deal.mechanicaleng || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'mechanicaleng')}
                  />
                )
              }
            ]}
          />
        </Col>
        <Col className={s.column} xl="4" lg="4" xs="12" sm="12" widths={[{ xl: '1400px' }]}>
          <KeyValuePairs
            pairs={[
              {
                key: 'Electrical Eng',
                value: (
                  <Input
                    type="text"
                    text={deal.electricaleng || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'electricaleng')}
                  />
                )
              },
              {
                key: 'Plumbing Eng',
                value: (
                  <Input
                    type="text"
                    text={deal.plumbingeng || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'plumbingeng')}
                  />
                )
              },
              {
                key: 'FP Eng',
                value: (
                  <Input
                    type="text"
                    text={deal.fpeng || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'fpeng')}
                  />
                )
              },
              {
                key: 'Other Eng',
                value: (
                  <Input
                    type="text"
                    text={deal.othereng || ''}
                    className={s.nameInput}
                    placeholder="Enter Company"
                    onChangeCb={() => updateState(event.target.value, 'othereng')}
                  />
                )
              }
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Button className={s.saveBtn} onClick={() => onSave()}>
          Save
        </Button>
      </Row>
      <div className={s.clearfix} />
    </Container>
  )
}

export default Details
